import * as React from "react";
import {
  TextBulletList20Regular,
  TextIndentDecrease20Regular,
  TextIndentIncrease20Regular,
} from "@fluentui/react-icons";
import {
  aufzaehlungUmschalten,
  ausruecken,
  einruecken,
  enterFortsetzen,
  hatAufzaehlung,
  type TextZustand,
} from "../../../utils/aufzaehlungText";

/**
 * useAufzaehlung — bindet {@link ../../../utils/aufzaehlungText} an ein
 * <textarea>, damit ein gewöhnliches Textfeld gegliederte Aufzählungen führen
 * kann: Knopf setzt einen Punkt, Enter setzt den nächsten fort, Tab rückt eine
 * Ebene ein, Shift+Tab wieder aus.
 *
 * Gespeichert wird dabei REINER TEXT (Zeichen `•`, Zeilenumbrüche, führende
 * Leerzeichen) — kein HTML. Damit bleibt der Wert überall lesbar, wo er außer
 * in dieser Maske noch auftaucht: Suche, Export, Beruf-Sektion verbundener
 * Personen.
 *
 * **Tab nur in Aufzählungszeilen.** Außerhalb behält Tab seine normale
 * Bedeutung und springt zum nächsten Feld — ein Textfeld, das die Tabulatortaste
 * bedingungslos schluckt, ist mit der Tastatur nicht mehr verlassbar.
 */
export function useAufzaehlung(value: string, onChange?: (v: string) => void) {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  /**
   * Die Auswahl nach einer programmatischen Änderung. Sie kann erst gesetzt
   * werden, wenn React den neuen Wert gerendert hat — vorher würde das
   * kontrollierte <textarea> sie beim Re-Render wieder ans Ende schieben.
   */
  const nachRender = React.useRef<{ start: number; end: number } | null>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    const ziel = nachRender.current;
    if (el && ziel) {
      el.setSelectionRange(ziel.start, ziel.end);
      nachRender.current = null;
    }
  });

  const zustand = (): TextZustand | null => {
    const el = ref.current;
    if (!el) return null;
    return { text: el.value, start: el.selectionStart, end: el.selectionEnd };
  };

  /** Wendet eine Operation an. `false`, wenn sie nicht zuständig war. */
  const anwenden = (fn: (z: TextZustand) => TextZustand | null): boolean => {
    if (!onChange) return false;
    const z = zustand();
    if (!z) return false;
    const r = fn(z);
    if (!r) return false;
    nachRender.current = { start: r.start, end: r.end };
    onChange(r.text);
    return true;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!onChange) return;
    if (e.key === "Enter" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      if (anwenden(enterFortsetzen)) e.preventDefault();
      return;
    }
    if (e.key === "Tab" && !e.altKey && !e.ctrlKey && !e.metaKey) {
      const z = zustand();
      // Kein Punkt in der Nähe → Tab bleibt Feldwechsel.
      if (!z || !hatAufzaehlung(z)) return;
      if (anwenden(e.shiftKey ? ausruecken : einruecken)) e.preventDefault();
    }
  };

  const aktionen = [
    { titel: "Aufzählungspunkt", Icon: TextBulletList20Regular, fn: aufzaehlungUmschalten },
    { titel: "Einrücken (Tab)", Icon: TextIndentIncrease20Regular, fn: einruecken },
    { titel: "Ausrücken (Umschalt+Tab)", Icon: TextIndentDecrease20Regular, fn: ausruecken },
  ].map((a) => ({ ...a, onClick: () => anwenden(a.fn) }));

  return { ref, onKeyDown, aktionen, bedienbar: !!onChange };
}

export type AufzaehlungsAktion = ReturnType<typeof useAufzaehlung>["aktionen"][number];

/**
 * Die Knopfleiste über dem Textfeld. Bewusst dezent und inline gestylt: Sie
 * erscheint in zwei Design-Welten (Anlage-Formular und Kontakt-Detailansicht),
 * die keine gemeinsame Klassenbasis haben.
 *
 * `onMouseDown` verhindert den Standard — sonst nähme der Knopf dem Textfeld
 * den Fokus, und mit ihm die Auswahl, auf die er wirken soll.
 */
export const AufzaehlungsLeiste: React.FC<{ aktionen: AufzaehlungsAktion[] }> = ({ aktionen }) => (
  <div style={{ display: "flex", gap: 4, marginBottom: 2 }} role="group" aria-label="Gliederung">
    {aktionen.map((a) => (
      <button
        key={a.titel}
        type="button"
        title={a.titel}
        aria-label={a.titel}
        onMouseDown={(e) => e.preventDefault()}
        onClick={a.onClick}
        style={{
          display: "grid",
          placeItems: "center",
          width: 30,
          height: 26,
          padding: 0,
          borderRadius: 7,
          border: "1.5px solid var(--line-2, #ece6ec)",
          background: "#fff",
          color: "var(--ink-2, #6b5a66)",
          cursor: "pointer",
        }}
      >
        <a.Icon />
      </button>
    ))}
  </div>
);
